import { FC } from 'react';
import styled from 'styled-components';
import Alert from 'components/Base/Alert';
import { Icon, TIconNames } from 'components/Base/Icon';
import { Text } from 'components/Base/Typography';
import { Colors, FontSizes, FontWeights } from 'core/CssVariables';
import { EAnimalTypesUI } from 'data/types/animal.types';
import { formatNotManagedTitle } from 'helpers/animalTypeRetraction';

/**
 * PP-10057. Explains that OurPetPolicy does not manage this animal type at the property, so the
 * resident's application was retracted — they did nothing wrong and the animal is not abandoned,
 * the property team handles it directly.
 *
 * The heading names the animal type because a property can exclude any combination of Pet, ESA and
 * Service animal, and is worded identically to the admin's preview of this block in the PM app. The
 * body differs by placement: onboarding shows the property's own configured wording, while the
 * residency card shows fixed guidance, so the caller supplies it.
 */

type TProps = {
  animalType?: EAnimalTypesUI | null;
  description: string;
};

const AnimalTypeNotManagedAlert: FC<TProps> = ({ animalType, description }) => (
  <SAlertWrapper>
    <Alert
      showIcon
      icon={
        <Icon
          icon={TIconNames.WARNING_FILLED}
          color={Colors.WarningColor}
          size={20}
        />
      }
      type="warning"
      message={formatNotManagedTitle(animalType)}
      description={<Text fontLevel={5}>{description}</Text>}
    />
  </SAlertWrapper>
);

/*
 * Pinning both line-heights and trimming the heading's bottom margin keeps the block optically
 * centred: antd's 16px heading carries more leading above its cap height than the smaller body
 * leaves below its baseline, so equal padding alone reads top-heavy.
 */
const SAlertWrapper = styled.div`
  /*
   * The ui-kit alert reserves 24px below itself for page-level alerts stacked above content. Every
   * placement of this block sits inside something that already spaces it — a card, an onboarding
   * step, the retraction dialog — so the reservation only detaches it from what it is warning
   * about. Needs to outrank the ui-kit's own && rule.
   */
  &&& .ant-alert {
    margin-bottom: 0;
  }

  .ant-alert-with-description {
    align-items: flex-start;
    padding: 20px 24px;
    background-color: ${Colors.LightYellow};
    border: 1px solid ${Colors.LightBorder};
  }

  /* Sits on the heading's line rather than centred against the whole block. */
  .ant-alert-with-description .ant-alert-icon {
    margin-right: 12px;
    font-size: inherit;
    line-height: 1.35;
    display: flex;
    align-items: center;
    align-self: flex-start;
    height: calc(1.35 * ${FontSizes.FontMD}px);
  }

  .ant-alert-with-description .ant-alert-message {
    margin-bottom: 2px;
    line-height: 1.35;
    font-weight: ${FontWeights.SemiBold};
    color: ${Colors.Black};
  }

  .ant-alert-with-description .ant-alert-description,
  .ant-alert-with-description .ant-alert-description * {
    line-height: 1.35;
    color: ${Colors.Black};
  }
`;

export default AnimalTypeNotManagedAlert;
